import { useState, useEffect } from 'react';
import { supabase } from './supabase';

const MONO = "'DM Mono', monospace";

export default function AuthForm({ onSuccess, defaultMode = 'signup' }) {
  const [mode, setMode] = useState(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);

  const isSignUp = mode === 'signup';
  const isSignIn = mode === 'signin';
  const isReset = mode === 'reset';
  const isMagicLink = mode === 'magic';

  // Catch OAuth errors on redirect (e.g. org_internal)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('error')) {
      const params = new URLSearchParams(hash.replace('#', ''));
      const errCode = params.get('error');
      const errDesc = params.get('error_description');
      if (errCode) {
        if (errCode === 'access_denied' && errDesc?.includes('org_internal')) {
          setError('Google sign-in is temporarily unavailable. Please use email/password or magic link instead.');
        } else {
          setError(errDesc || `Sign-in failed: ${errCode}`);
        }
        // Clean the URL
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, []);

  const handleGoogle = async () => {
    setGoogleLoading(true);
    setError('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        queryParams: { prompt: 'select_account' },
        scopes: 'openid email profile',
      }
    });
    if (error) {
      setError(error.message);
      setGoogleLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email.trim()) { setError('Enter your email address'); return; }
    setError(''); setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: { emailRedirectTo: window.location.origin }
      });
      if (error) throw error;
      setMessage('Magic link sent! Check your email and click the link to sign in.');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(''); setMessage(''); setLoading(true);
    try {
      if (isMagicLink) {
        return handleMagicLink();
      } else if (isSignIn) {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onSuccess(data.user);
      } else if (isSignUp) {
        const { data, error } = await supabase.auth.signUp({
          email, password,
          options: { emailRedirectTo: window.location.origin }
        });
        if (error) throw error;
        if (data.user && !data.user.confirmed_at) {
          setMessage('Check your email to confirm your account, then sign in.');
          setMode('signin');
        } else {
          onSuccess(data.user);
        }
      } else if (isReset) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin + '?reset=true'
        });
        if (error) throw error;
        setMessage('Password reset link sent — check your email.');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', fontSize: 14, padding: '13px 14px',
    background: 'rgba(10,15,30,0.9)',
    border: '1px solid #1e2a40', borderRadius: 10,
    color: '#f0f4ff', caretColor: '#4f7cff',
    fontFamily: MONO, outline: 'none',
  };

  // Different accent color per mode
  const accent = isSignUp ? '#10b981' : '#4f7cff';
  const accentBg = isSignUp ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#4f7cff,#3a5fd9)';

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* Mode toggle tabs */}
      {!isReset && !isMagicLink && (
        <div style={{ display: 'flex', background: 'rgba(10,15,30,0.8)', borderRadius: 10, padding: 3, border: '1px solid #1e2a40', marginBottom: 4 }}>
          <button onClick={() => { setMode('signup'); setError(''); setMessage(''); }}
            style={{ flex: 1, padding: '8px', borderRadius: 8, fontSize: 11, fontFamily: MONO, letterSpacing: '0.08em', fontWeight: 700,
              background: isSignUp ? accent : 'transparent',
              color: isSignUp ? '#fff' : '#5a7090', border: 'none', cursor: 'pointer', transition: 'all 0.2s' }}>
            CREATE ACCOUNT
          </button>
          <button onClick={() => { setMode('signin'); setError(''); setMessage(''); }}
            style={{ flex: 1, padding: '8px', borderRadius: 8, fontSize: 11, fontFamily: MONO, letterSpacing: '0.08em', fontWeight: 700,
              background: isSignIn ? accent : 'transparent',
              color: isSignIn ? '#fff' : '#5a7090', border: 'none', cursor: 'pointer', transition: 'all 0.2s' }}>
            SIGN IN
          </button>
        </div>
      )}

      {/* Description */}
      <div style={{ fontSize: 11, color: '#5a7090', fontFamily: MONO, textAlign: 'center', letterSpacing: '0.06em', marginBottom: 2 }}>
        {isSignUp && 'New to MyRelay? Create your free account.'}
        {isSignIn && 'Welcome back. Sign in to continue.'}
        {isReset && 'Enter your email to reset your password.'}
        {isMagicLink && 'Enter your email — we\'ll send you a sign-in link.'}
      </div>

      {/* Google button */}
      {!isReset && !isMagicLink && (
        <button onClick={handleGoogle} disabled={googleLoading}
          style={{ width: '100%', padding: '13px', borderRadius: 12,
            background: isSignUp ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${isSignUp ? 'rgba(16,185,129,0.25)' : '#2a3a55'}`,
            color: '#f0f4ff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            fontFamily: 'system-ui, sans-serif' }}>
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {googleLoading ? 'Redirecting…' : isSignUp ? 'Sign up with Google' : 'Continue with Google'}
        </button>
      )}

      {/* Divider */}
      {!isReset && !isMagicLink && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, height: 1, background: '#1e2a40' }}/>
          <span style={{ fontSize: 10, color: '#2a3a55', fontFamily: MONO, letterSpacing: '0.1em' }}>OR</span>
          <div style={{ flex: 1, height: 1, background: '#1e2a40' }}/>
        </div>
      )}

      {/* Error / Message */}
      {error && <div style={{ background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.3)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#f43f5e', fontFamily: MONO }}>{error}</div>}
      {message && <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#10b981', fontFamily: MONO }}>{message}</div>}

      {/* Email */}
      <input type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="Email address" style={inputStyle}
        onKeyDown={e => e.key === 'Enter' && handleSubmit()}/>

      {/* Password — hidden for magic link and reset */}
      {!isReset && !isMagicLink && (
        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
          placeholder={isSignUp ? 'Create a password' : 'Password'} style={inputStyle}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}/>
      )}

      {/* Submit */}
      <button onClick={isMagicLink ? handleMagicLink : handleSubmit} disabled={loading}
        style={{ width: '100%', padding: '14px', borderRadius: 12, background: accentBg,
          color: '#fff', fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer',
          fontFamily: MONO, letterSpacing: '0.06em', opacity: loading ? 0.7 : 1 }}>
        {loading ? 'Please wait…'
          : isMagicLink ? 'Send Magic Link →'
          : isSignUp ? 'Create Account →'
          : isSignIn ? 'Sign In →'
          : 'Send Reset Link →'}
      </button>

      {/* Footer links */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, fontSize: 11, fontFamily: MONO, color: '#2a3a55', marginTop: 2, flexWrap: 'wrap' }}>
        {isSignIn && (
          <>
            <button onClick={() => { setMode('reset'); setError(''); setMessage(''); }}
              style={{ color: '#5a7090', background: 'none', fontSize: 11, fontFamily: MONO }}>
              Forgot password?
            </button>
            <button onClick={() => { setMode('magic'); setError(''); setMessage(''); }}
              style={{ color: '#4f7cff', background: 'none', fontSize: 11, fontFamily: MONO }}>
              Use magic link
            </button>
          </>
        )}
        {isSignUp && (
          <button onClick={() => { setMode('magic'); setError(''); setMessage(''); }}
            style={{ color: '#4f7cff', background: 'none', fontSize: 11, fontFamily: MONO }}>
            Sign up without password
          </button>
        )}
        {(isReset || isMagicLink) && (
          <button onClick={() => { setMode('signin'); setError(''); setMessage(''); }}
            style={{ color: '#4f7cff', background: 'none', fontSize: 11, fontFamily: MONO }}>
            ← Back to sign in
          </button>
        )}
      </div>

    </div>
  );
}
